import { clearBrowserCookies, clearWebStorage } from '@/lib/storage/browserStorage';
import { NEXT_PUBLIC_OPENAI_ADS_DISABLED, NEXT_PUBLIC_OPENAI_ADS_PIXEL_ID } from '@/lib/env';
import logger, { isOpenAIAdsLoggingEnabled } from './logger';

// OpenAI Ads (oaiq) first-party storage: the `__oppref` attribution cookie (30-day TTL) and the
// `__oaiq_domain_probe` probe cookie the SDK sets on init. Matched by prefix so withdraw teardown clears the full set, kept disjoint from other services' key-sets (Snapchat sc_*/_scid*, GTM
// _ga*/_gcl*, Amplitude AMP_*, Hotjar _hj*).
const OPENAI_ADS_KEY_PREFIXES = ['__oppref', '__oaiq'];

let enabled = false;

export const isOpenAIAdsConfigured = (): boolean =>
  !NEXT_PUBLIC_OPENAI_ADS_DISABLED && Boolean(NEXT_PUBLIC_OPENAI_ADS_PIXEL_ID?.trim());

export const isOpenAIAdsEnabled = (): boolean => enabled && isOpenAIAdsConfigured();

const isOpenAIAdsKey = (key: string): boolean =>
  OPENAI_ADS_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));

const clearOpenAIAdsStorage = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const cookiesRemoved = clearBrowserCookies(isOpenAIAdsKey);
  const localRemoved = clearWebStorage(() => window.localStorage, isOpenAIAdsKey);
  const sessionRemoved = clearWebStorage(() => window.sessionStorage, isOpenAIAdsKey);

  logger.debug('Cleared OpenAI Ads storage', { cookiesRemoved, localRemoved, sessionRemoved });
};

export const enableOpenAIAds = (): boolean => {
  const configured = isOpenAIAdsConfigured();
  enabled = typeof window !== 'undefined' && configured;
  // Heartbeat on consent grant so it is clear the pixel is wired even before a conversion fires.
  // Guarded so the context object is only allocated when logging is enabled.
  if (isOpenAIAdsLoggingEnabled) {
    logger.debug('OpenAI Ads enable requested', { enabled, configured });
  }
  return enabled;
};

export const disableOpenAIAds = (): void => {
  enabled = false;
  // Mirrors Snapchat/Convert/Hotjar teardown: storage is cleared and event calls are gated off.
  // A loaded vendor runtime may persist until a full page reload.
  logger.debug('OpenAI Ads disabled: stopping tracking calls and clearing storage');
  clearOpenAIAdsStorage();
};
